import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import ProjectsTable from "@/components/projects/ProjectsTable";
import {
  Folder,
  Clock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Roles } from "@/enums/roles.enum";
import { ProjectStatus } from "@/enums/project.enum";
import { useDeviceType } from "@/hooks/use-mobile";
import {
  MobileContainer,
  MobileSpacing,
} from "@/components/mobile/ResponsiveLayout";
import MobileCard from "@/components/mobile/MobileCard";
import { AnalyticsDashboard } from "@/components/dashboard/AnalyticsDashboard";

const Dashboard = () => {
  const { user } = useAuth();
  const { projects, isLoading } = useProjects();
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();

  // Filter out completed and rejected projects by default for dashboard display
  const filteredProjects = projects.filter(
    (proj) => proj.status !== ProjectStatus.COMPLETED && proj.status !== ProjectStatus.REJECTED
  );

  const draftProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.DRAFT
  ).length;
  const pendingProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.PENDING
  ).length;
  const submittedProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.SUBMITTED
  ).length;
  const inProgressProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.IN_PROGRESS
  ).length;
  const completedProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.COMPLETED
  ).length;
  const onHoldProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.ON_HOLD
  ).length;
  const rejectedProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.REJECTED
  ).length;
  const totalProjects = projects.length;

  const statsData = [
    {
      label: "Draft",
      value: draftProjects,
      icon: Folder,
      color: "text-muted-foreground",
    },
    {
      label: "Pending",
      value: pendingProjects,
      icon: Folder,
      color: "text-status-pending",
    },
    {
      label: "Submitted",
      value: submittedProjects,
      icon: Folder,
      color: "text-status-submitted",
    },
    {
      label: "In Progress",
      value: inProgressProjects,
      icon: Folder,
      color: "text-status-inProgress",
    },
    {
      label: "Completed",
      value: completedProjects,
      icon: CheckCircle,
      color: "text-status-approved",
    },
    {
      label: "On Hold",
      value: onHoldProjects,
      icon: Clock,
      color: "text-status-pending",
    },
    {
      label: "Rejected",
      value: rejectedProjects,
      icon: AlertTriangle,
      color: "text-status-rejected",
    },
    {
      label: "Total",
      value: totalProjects,
      icon: Folder,
      color: "text-muted-foreground",
    },
  ];

  if (isMobile) {
    return (
      <MobileContainer>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold">Dashboard</h1>

          {/* Stats Grid - Mobile optimized */}
          <div className="grid gap-3 grid-cols-2">
            {statsData.map((stat) => {
              const Icon = stat.icon;
              const getNavigationPath = () => {
                if (stat.label === "Draft") return "/projects?status=draft";
                if (stat.label === "Total") return "/projects";
                if (stat.label === "Pending") return "/projects?status=pending";
                if (stat.label === "Submitted")
                  return "/projects?status=submitted";
                if (stat.label === "In Progress")
                  return "/projects?status=in_progress";
                if (stat.label === "Completed")
                  return "/projects?status=completed";
                if (stat.label === "On Hold") return "/projects?status=on_hold";
                if (stat.label === "Rejected")
                  return "/projects?status=rejected";
                return "/projects";
              };

              return (
                <MobileCard
                  key={stat.label}
                  className="p-3 cursor-pointer hover:shadow-md transition-shadow duration-200"
                  onClick={() => navigate(getNavigationPath())}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      {isLoading ? (
                        <>
                          <div className="h-6 w-12 bg-muted animate-pulse rounded mb-1" />
                          <div className="h-3 w-16 bg-muted animate-pulse rounded" />
                        </>
                      ) : (
                        <>
                          <div className="text-xl font-bold">{stat.value}</div>
                          <p className="text-xs text-muted-foreground">
                            {stat.label}
                          </p>
                        </>
                      )}
                    </div>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </MobileCard>
              );
            })}
          </div>

          <MobileSpacing size="md" />

          {/* Recent Projects Table */}
          <ProjectsTable
            projects={filteredProjects}
            isLoading={isLoading}
            title="Recent Projects"
            description="Latest updates on your projects"
            showNewButton={
              user?.role === Roles.ADMIN ||
              user?.role === Roles.VENDOR ||
              user?.role === Roles.CLIENT
            }
          />

          {/* Bottom spacing for navigation */}
          <div className="h-20" />
        </div>
      </MobileContainer>
    );
  }

  // Desktop layout (original)
  return (
    <div className="space-y-8 p-6 md:p-8 bg-background/50 min-h-screen">
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of your project status and recent activity.
        </p>
      </div>

      {/* New Analytics Dashboard with Customizable Widgets */}
      <AnalyticsDashboard />

      <ProjectsTable
        projects={filteredProjects}
        isLoading={isLoading}
        title="Recent Projects"
        description="Latest updates on your projects"
        showNewButton={
          user?.role === Roles.ADMIN ||
          user?.role === Roles.VENDOR ||
          user?.role === Roles.CLIENT
        }
      />
    </div>
  );
};

export default Dashboard;
